import React, { useState } from "react";
import { Search, Trash2, AlertTriangle } from "lucide-react";
import { api } from "./api";
import { Eyebrow, BoutonSombre, Case, Badge } from "./ui";
import { useConfirmation } from "./Confirmation";

/* Ménage des lignes de test : on cherche un motif (« test », un faux nom), on montre tout
   ce qui correspond des deux côtés (site et copie console), et on efface uniquement ce
   qui a été coché. Jamais de suppression en aveugle. */
export default function ReglagesMenage() {
  const { informer, demander } = useConfirmation();
  const [motif, setMotif] = useState("test");
  const [data, setData] = useState(null);
  const [choisis, setChoisis] = useState([]);
  const [enCours, setEnCours] = useState(false);

  const chercher = async () => {
    setEnCours(true);
    try {
      const d = await api(`/menage/apercu?motif=${encodeURIComponent(motif)}`);
      setData(d);
      // Rien n'est coché d'avance : la recherche « test » trouve aussi « Testuz » ou
      // « attestation », et la suppression est définitive (audit du 10.10.2026).
      setChoisis([]);
    } catch (e) {
      await informer({ titre: "Recherche impossible", ton: "attention", texte: e.message });
    } finally {
      setEnCours(false);
    }
  };

  const supprimer = async () => {
    const ok = await demander({
      titre: `Supprimer ${choisis.length} ligne(s) ?`,
      ton: "danger",
      texte: "Ces lignes seront effacées définitivement de la base. L'historique garde la trace de la suppression.",
      points: [data.avertissement],
      confirmer: "Supprimer",
    });
    if (!ok) return;
    setEnCours(true);
    try {
      const r = await api("/menage/supprimer", { method: "POST", body: JSON.stringify({ cles: choisis }) });
      await informer({
        titre: `${r.total} ligne(s) supprimée(s)`, ton: "succes",
        texte: "La base ne contient plus ces lignes de test.",
      });
      await chercher();
    } catch (e) {
      await informer({ titre: "Suppression impossible", ton: "attention", texte: e.message });
    } finally {
      setEnCours(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl p-5 border" style={{ borderColor: "var(--border)" }} data-testid="menage-bloc">
      <Eyebrow>Chercher les lignes de test</Eyebrow>
      <div className="flex flex-wrap items-center gap-3 mt-2">
        <input
          className="input-field" style={{ width: 260, paddingTop: 8, paddingBottom: 8 }}
          value={motif} onChange={(e) => setMotif(e.target.value)}
          placeholder="test, TEST_, un faux nom…" data-testid="menage-motif"
        />
        <BoutonSombre onClick={chercher} testid="menage-chercher" disabled={enCours || motif.trim().length < 3}>
          <Search size={14} /> Chercher
        </BoutonSombre>
        {data && (
          <span className="text-xs text-[var(--muted)]" data-testid="menage-resume">
            {data.nb} ligne(s) — {data.nb_site} côté site, {data.nb_console} côté console
          </span>
        )}
      </div>

      {data && data.nb === 0 && (
        <p className="text-sm text-[var(--muted)] mt-4" data-testid="menage-vide">
          Aucune ligne ne contient « {data.motif} ». Rien à nettoyer.
        </p>
      )}

      {data && data.nb > 0 && (
        <>
          <div className="rounded-2xl px-4 py-3 text-xs mt-4 flex gap-2"
            style={{ background: "#FFFBEB", color: "#92400E" }} data-testid="menage-avertissement">
            <AlertTriangle size={16} className="shrink-0 mt-0.5" />
            <span>{data.avertissement}</span>
          </div>

          <div className="flex items-center gap-3 mt-4">
            <Case
              cochee={choisis.length === data.lignes.length}
              onChange={(v) => setChoisis(v ? data.lignes.map((l) => l.cle) : [])}
              testid="menage-tout"
            />
            <span className="text-xs text-[var(--muted)]">Tout sélectionner ({data.lignes.length})</span>
            <BoutonSombre onClick={supprimer} testid="menage-supprimer"
              disabled={enCours || !choisis.length} danger>
              <Trash2 size={14} /> Supprimer {choisis.length} ligne(s)
            </BoutonSombre>
          </div>

          <ul className="mt-3 divide-y border rounded-2xl overflow-hidden" style={{ borderColor: "var(--border)" }}>
            {data.lignes.map((l) => (
              <li key={l.cle} className="flex flex-wrap items-center gap-3 px-4 py-2.5 text-sm"
                data-testid="menage-ligne">
                <Case
                  cochee={choisis.includes(l.cle)}
                  onChange={(v) => setChoisis(v ? [...choisis, l.cle] : choisis.filter((x) => x !== l.cle))}
                  testid="menage-case"
                />
                <Badge ton={l.cote === "site" ? "rouge" : "bleu"}>{l.cote === "site" ? "SITE" : "CONSOLE"}</Badge>
                <div className="min-w-[220px]">
                  <div className="font-medium">{l.libelle}</div>
                  <div className="text-xs text-[var(--muted)]">{l.quoi}{l.detail ? ` · ${l.detail}` : ""}</div>
                </div>
                <span className="ml-auto text-xs text-[var(--muted)]">{l.quand || "—"}</span>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
